import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Provider, ProviderType } from '../types/referral'
import { providerService } from '../services/providerService'
import { ProviderForm } from './ProviderForm'
import './ProviderDirectory.css'

interface ProviderDirectoryProps {
  onProviderSelect?: (provider: Provider) => void
  showSelectButton?: boolean
}

export function ProviderDirectory({
  onProviderSelect,
  showSelectButton = false,
}: ProviderDirectoryProps) {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingProvider, setEditingProvider] = useState<Provider | undefined>()
  const [typeFilter, setTypeFilter] = useState<ProviderType | 'all'>('all')
  const [specialtyFilter, setSpecialtyFilter] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data: providers, isLoading, error } = useQuery({
    queryKey: ['providers'],
    queryFn: () => providerService.getAll({ isActive: true }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => providerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] })
    },
  })

  // Get unique specialties for filter
  const specialties = Array.from(
    new Set(providers?.map((p) => p.specialty).filter(Boolean) || [])
  ).sort()

  // Apply filters
  const filteredProviders = providers?.filter((p) => {
    if (typeFilter !== 'all' && p.type !== typeFilter) return false
    if (specialtyFilter && p.specialty !== specialtyFilter) return false
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      const nameParts = [p.fullName, p.name, p.firstName, p.lastName].filter(Boolean).join(' ')
      return (
        nameParts.toLowerCase().includes(search) ||
        p.organization?.toLowerCase().includes(search) ||
        p.specialty?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search)
      )
    }
    return true
  })

  const handleNewProvider = () => {
    setEditingProvider(undefined)
    setShowForm(true)
  }

  const handleEditProvider = (provider: Provider) => {
    setEditingProvider(provider)
    setShowForm(true)
  }

  const handleDeleteProvider = (id: string) => {
    if (window.confirm('Are you sure you want to deactivate this provider?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleSelectProvider = (provider: Provider) => {
    onProviderSelect?.(provider)
  }

  if (showForm) {
    return (
      <div className="provider-directory-form">
        <ProviderForm
          initialData={editingProvider}
          onSubmit={async (data) => {
            if (editingProvider) {
              await providerService.update(editingProvider.id, data)
            } else {
              await providerService.create(data)
            }
            setShowForm(false)
            setEditingProvider(undefined)
            queryClient.invalidateQueries({ queryKey: ['providers'] })
          }}
          onCancel={() => {
            setShowForm(false)
            setEditingProvider(undefined)
          }}
        />
      </div>
    )
  }

  if (isLoading) {
    return <div className="provider-directory-loading">Loading providers...</div>
  }

  if (error) {
    return <div className="provider-directory-error">Error loading providers</div>
  }

  return (
    <div className="provider-directory">
      <div className="provider-directory-header">
        <h2>Provider Directory</h2>
        <button className="btn-primary" onClick={handleNewProvider}>
          Add Provider
        </button>
      </div>

      <div className="provider-directory-filters">
        <div className="filter-group">
          <label htmlFor="typeFilter">Type</label>
          <select
            id="typeFilter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ProviderType | 'all')}
          >
            <option value="all">All</option>
            <option value="internal">Internal</option>
            <option value="external">External</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="specialtyFilter">Specialty</label>
          <select
            id="specialtyFilter"
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
          >
            <option value="">All Specialties</option>
            {specialties.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Search</label>
          <input
            id="searchTerm"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search providers..."
          />
        </div>
      </div>

      {filteredProviders && filteredProviders.length > 0 ? (
        <div className="provider-directory-grid">
          {filteredProviders.map((provider) => {
            const providerName = provider.fullName || provider.name
            return (
              <div key={provider.id} className="provider-card">
                <div className="provider-card-header">
                  <h3>
                    {provider.title ? `${provider.title} ` : ''}
                    {providerName}
                  </h3>
                  <span className={`provider-type-badge type-${provider.type}`}>
                    {provider.type}
                  </span>
                </div>

              <div className="provider-card-content">
                {provider.organization && (
                  <div className="provider-info">
                    <strong>Organization:</strong> {provider.organization}
                  </div>
                )}
                {provider.specialty && (
                  <div className="provider-info">
                    <strong>Specialty:</strong> {provider.specialty}
                  </div>
                )}
                {provider.address && (
                  <div className="provider-info">
                    <strong>Address:</strong>{' '}
                    {[
                      provider.address.street,
                      provider.address.city,
                      provider.address.state,
                      provider.address.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
                {provider.phone && (
                  <div className="provider-info">
                    <strong>Phone:</strong> {provider.phone}
                  </div>
                )}
                {provider.email && (
                  <div className="provider-info">
                    <strong>Email:</strong> {provider.email}
                  </div>
                )}
                {provider.healthmailAddress && (
                  <div className="provider-info">
                    <strong>Healthmail:</strong> {provider.healthmailAddress}
                  </div>
                )}
              </div>

                <div className="provider-card-actions">
                  {showSelectButton && (
                    <button
                      className="btn-primary"
                      onClick={() => handleSelectProvider(provider)}
                    >
                    Select
                  </button>
                )}
                <button
                  className="btn-secondary"
                  onClick={() => handleEditProvider(provider)}
                >
                  Edit
                </button>
                <button
                  className="btn-danger"
                  onClick={() => handleDeleteProvider(provider.id)}
                  disabled={deleteMutation.isPending}
                >
                  Deactivate
                </button>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="provider-directory-empty">
          <p>No providers found</p>
          <button className="btn-primary" onClick={handleNewProvider}>
            Add First Provider
          </button>
        </div>
      )}
    </div>
  )
}
